import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from 'i18next';

dayjs.extend(utc);

type TemplateType = 'YYYY-MM-DD' | 'MMM D, YYYY, h:mm A' | 'h:mm A' | 'YYYYMMDD';

// Used to format date
export function formatDate(date: dayjs.ConfigType, template: TemplateType = 'YYYY-MM-DD') {
  if (!date) return '';

  const dayjsObj = dayjs(date);
  const valid = dayjsObj.isValid();
  if (!valid) return '';

  return dayjsObj.format(template);
}

// Used to format date and time
export function formatDateToCalendar(date?: number | string | Date, showTime: boolean = true) {
  if (!date) return '';

  const dayjsObj = dayjs(date);
  const valid = dayjsObj.isValid();
  if (!valid) return '';

  const nowDayjsObj = dayjs();
  const diffDay = dayjsObj.startOf('day').diff(nowDayjsObj.startOf('day'), 'day');
  const diffYear = dayjsObj.startOf('year').diff(nowDayjsObj.startOf('year'), 'year');

  const isToday = diffDay === 0;
  const isTomorrow = diffDay === 1;
  const isYesterday = diffDay === -1;
  const isThisYear = diffYear === 0;

  const value = new Date(date);
  if (showTime) {
    if (isToday) return i18n.t('date.render_with_count_and_hhmm', { value, ns: 'dateNumber', count: 0 });
    if (isTomorrow) return i18n.t('date.render_with_count_and_hhmm', { value, ns: 'dateNumber', count: 1 });
    if (isYesterday) return i18n.t('date.render_with_count_and_hhmm', { value, ns: 'dateNumber', count: -1 });
    if (isThisYear) return i18n.t('date.render_with_MMMddhhmm', { value, ns: 'dateNumber' });
    return i18n.t('date.render_with_yyyyMMMddhhmm', { value, ns: 'dateNumber' });
  } else {
    if (isToday) return i18n.t('date.today', { ns: 'dateNumber' });
    if (isTomorrow) return i18n.t('date.tomorrow', { ns: 'dateNumber' });
    if (isYesterday) return i18n.t('date.yesterday', { ns: 'dateNumber' });
    if (isThisYear) return i18n.t('date.render_with_MMMdd', { value, ns: 'dateNumber' });
    return i18n.t('date.render_with_yyyyMMMdd', { value, ns: 'dateNumber' });
  }
}

// Used to send UTC date and time to the back-end
export function formatDateToUTC(date: dayjs.ConfigType) {
  if (!date) return '';

  const dayjsObj = dayjs(date);
  const valid = dayjsObj.isValid();
  if (!valid) return '';

  return dayjsObj.utc().format();
}

export function formatDateToUTC_UNIX(date: dayjs.ConfigType) {
  if (!date) return 0;

  const dayjsObj = dayjs(date);
  const valid = dayjsObj.isValid();
  if (!valid) return 0;

  return dayjsObj.utc().unix() * 1000;
}

export function prettyBytes(bytes: number, decimals = 2, k = 1024) {
  if (bytes === 0) return '0B';
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + '' + sizes[i];
}

export const scrollToBottom = (el: string | Element) => {
  const newEl = el instanceof Element ? el : document.querySelector(el);
  if (newEl) {
    const { scrollHeight, clientHeight, scrollTop } = newEl;
    if (scrollHeight - clientHeight > scrollTop) {
      newEl.scrollTop = scrollHeight - clientHeight;
    }
  }
};

export type valueType = string | string[] | undefined;
export const JudgeHasValue = (options: any[], value: valueType, fieldKey?: string) => {
  if (
    !options.length ||
    !value ||
    (fieldKey && /create_at|update_at|breach_at/.test(fieldKey)) ||
    (fieldKey && /dependent_field/.test(fieldKey))
  )
    return value;
  if (Array.isArray(value)) {
    const keyItem: string[] = [];
    value.forEach((item) => {
      const items = options.find((option) => option.value === item);
      if (items) {
        keyItem.push(item);
      }
    });
    return keyItem;
  } else {
    const keyItem = options.find((option) => option.value === value);
    if (keyItem) return value;
    return undefined;
  }
};

// Concatenate spaces in a string with a hyphen
export const convertSymlink = (value: string) => {
  return value.toLocaleLowerCase().trim().replace(/ +/g, '-');
};

// Pendo data-bi attribute
export const getDataPrefix = (key: string, dataPrefix: string) => {
  let result = undefined;
  switch (key) {
    case 'form_id':
      result = `${dataPrefix}-form`;
      break;
    case 'priority':
      result = `${dataPrefix}-priority`;
      break;
    case 'about':
      result = `${dataPrefix}-about`;
      break;
    default:
      break;
  }
  return result;
};
